// src/components/sales/pages/scheduled-messages/ScheduledMessagesView.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, RefreshCw, X, Eye, Ban, ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "ALL", labelKey: "sales.scheduledMessages.status.all" },
  { value: "Pending", labelKey: "sales.scheduledMessages.status.pending" },
  { value: "Scheduled", labelKey: "sales.scheduledMessages.status.scheduled" },
  { value: "Queued", labelKey: "sales.scheduledMessages.status.queued" },
  { value: "Sent", labelKey: "sales.scheduledMessages.status.sent" },
  { value: "Delivered", labelKey: "sales.scheduledMessages.status.delivered" },
  { value: "Failed", labelKey: "sales.scheduledMessages.status.failed" },
  { value: "Canceled", labelKey: "sales.scheduledMessages.status.canceled" },
];

const norm = (v) => String(v ?? "").trim().toLowerCase();
const CANCELABLE = new Set(["pending", "scheduled", "queued"]);

function getStatusStyle(status) {
  const s = norm(status);
  if (s === "sent" || s === "delivered")
    return "bg-green-500/20 text-green-300 border-green-400/30";
  if (s === "failed")
    return "bg-red-500/20 text-red-300 border-red-400/30";
  if (s === "pending" || s === "scheduled" || s === "queued")
    return "bg-amber-500/20 text-amber-200 border-amber-400/30";
  if (s === "canceled" || s === "cancelled")
    return "bg-gray-500/20 text-gray-400 border-gray-400/20";
  return "bg-white/10 text-white border-white/20";
}

// ✅ Treat API datetime without TZ as UTC
function parseApiDate(iso) {
  if (!iso) return null;
  const s = String(iso).trim();
  const hasTz = /([zZ]|[+-]\d{2}:\d{2})$/.test(s);
  const d = new Date(hasTz ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatApiDate(iso) {
  const d = parseApiDate(iso);
  if (!d) return String(iso ?? "-");
  return d.toLocaleString(undefined, { timeZoneName: "short" });
}

export default function ScheduledMessagesView({
  loading,
  messages,
  totalCount = 0,      // ✅ جديد
  page = 1,            // ✅ جديد
  pageSize = 5,       // ✅ جديد
  totalPages = 1,      // ✅ جديد
  onPageChange,        // ✅ جديد

  clientMap,
  templateMap,

  statusFilter,
  setStatusFilter,
  onApplyFilter,
  onClearFilter,
  onRefresh,
  onCreateClick,
  onView,
  onCancel,

  message,
  onClearMessage,
}) {
  const { t, i18n } = useTranslation();
  void i18n;

  const rows = Array.isArray(messages) ? messages : [];

  const getRowId = (r) =>
    r?.id ?? r?.scheduledMessageId ?? r?.messageId ?? r?.scheduledMessagesId ?? null;

  const getClientLabel = (clientId) => {
    const c = clientMap?.get?.(String(clientId));
    if (!c) return String(clientId ?? "-");
    const name = `${c.firstName || ""} ${c.secondName || ""}`.trim();
    const company = c.companyName || "";
    return company ? `${company}${name ? ` — ${name}` : ""}` : name || String(clientId ?? "-");
  };

  const getTemplateLabel = (templateId) => {
    const tItem = templateMap?.get?.(String(templateId));
    if (!tItem) return String(templateId ?? "-");
    return tItem.templateName || tItem.name || String(templateId ?? "-");
  };

  const canCancelRow = (row) => CANCELABLE.has(norm(row?.status ?? row?.state));

  const headers = useMemo(
    () => [
      t("sales.scheduledMessages.table.client"),
      t("sales.scheduledMessages.table.template"),
      t("sales.scheduledMessages.table.channel"),
      t("sales.scheduledMessages.table.scheduling"),
      t("sales.scheduledMessages.table.whenTrigger"),
      t("sales.scheduledMessages.table.status"),
      t("sales.scheduledMessages.table.actions"),
    ],
    [t]
  );

  // ✅ Pagination display helpers
  const safeTotal = Number.isFinite(totalCount) ? totalCount : rows.length;
  const safePageSize = Number.isFinite(pageSize) ? pageSize : 10;
  const safePage = Number.isFinite(page) ? page : 1;

  const startIndex = safeTotal === 0 ? 0 : (safePage - 1) * safePageSize + 1;
  const endIndex = Math.min(safeTotal, safePage * safePageSize);

  const pageButtons = useMemo(() => {
    const tp = Math.max(1, Number(totalPages) || 1);
    const p = Math.min(Math.max(1, safePage), tp);

    // نافذة 5 أزرار
    const windowSize = 5;
    let start = Math.max(1, p - Math.floor(windowSize / 2));
    let end = start + windowSize - 1;
    if (end > tp) {
      end = tp;
      start = Math.max(1, end - windowSize + 1);
    }
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [safePage, totalPages]);

  return (
    <div className="text-white">
      {/* Header */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">{t("sales.scheduledMessages.title")}</h2>
          <p className="text-xs text-white/70">{t("sales.scheduledMessages.subtitle")}</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onCreateClick}
            className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <Plus className="w-5 h-5" />
            {t("sales.scheduledMessages.actions.newMessage")}
          </button>

          <button
            type="button"
            onClick={onRefresh}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            {t("common.refresh")}
          </button>
        </div>
      </div>

      {/* Optional Message Banner */}
      {message?.text ? (
        <div
          className={[
            "mb-6 rounded-2xl px-4 py-3 text-sm border backdrop-blur-xl flex items-start justify-between gap-3",
            message.type === "error"
              ? "border-red-500/30 bg-red-500/10 text-red-200"
              : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
          ].join(" ")}
        >
          <div className="whitespace-pre-wrap">{message.text}</div>
          {onClearMessage && (
            <button
              type="button"
              onClick={onClearMessage}
              className="shrink-0 p-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/10"
              title={t("sales.scheduledMessages.actions.dismiss")}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : null}

      {/* Filters */}
      <div className="mb-5 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-xl p-4">
        <div className="flex flex-col md:flex-row gap-3 md:items-end md:justify-between">
          <div className="min-w-[220px]">
            <label className="text-xs text-white/70">
              {t("sales.scheduledMessages.filters.statusLabel")}
            </label>
            <select
              className={inputCls + " mt-2"}
              value={statusFilter}
              onChange={(e) => setStatusFilter?.(e.target.value)}
              disabled={loading}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="text-black">
                  {t(o.labelKey)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onApplyFilter}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white backdrop-blur-lg disabled:opacity-50"
            >
              {t("sales.scheduledMessages.actions.apply")}
            </button>

            <button
              type="button"
              onClick={onClearFilter}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white backdrop-blur-lg disabled:opacity-50"
            >
              {t("sales.scheduledMessages.actions.clear")}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_40px_rgba(255,0,255,0.20)] border border-white/20 overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-pink-500/20 to-purple-600/30 backdrop-blur-xl flex items-center justify-between">
          <h3 className="text-lg font-bold">{t("sales.scheduledMessages.listTitle")}</h3>

          {/* ✅ إجمالي الرسائل مش عدد الصفحة */}
          <span className="text-sm text-gray-200">
            {t("sales.scheduledMessages.totalCount", { total: safeTotal })}
          </span>
        </div>

        <div className="p-0">
          <table className="w-full table-fixed text-white">
            <thead className="bg-white/10">
              <tr>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[18%]">{headers[0]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[18%]">{headers[1]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[10%]">{headers[2]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[14%]">{headers[3]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[18%]">{headers[4]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[10%]">{headers[5]}</th>
                <th className="px-4 py-3 text-right text-sm font-semibold w-[12%]">{headers[6]}</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-white/10">
              {rows.map((r, idx) => {
                const id = getRowId(r);
                const rowKey = id != null ? String(id) : `row-${idx}`;

                const clientId = r?.clientId;
                const templateId = r?.templateId;

                const channel = r?.channelType ?? "-";
                const scheduling = r?.schedulingType ?? "-";

                const when =
                  r?.scheduledDate
                    ? formatApiDate(r.scheduledDate)
                    : r?.triggerType
                      ? String(r.triggerType)
                      : "-";

                const status = r?.status ?? r?.state ?? "-";
                const cancelable = canCancelRow(r);

                return (
                  <tr key={rowKey} className="hover:bg-white/10 transition">
                    <td className="px-4 py-3 text-right align-top">
                      <div className="min-w-0">
                        <div className="text-sm break-words">{getClientLabel(clientId)}</div>
                        <div className="text-xs text-white/60 truncate">#{clientId ?? "-"}</div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <div className="min-w-0">
                        <div className="text-sm break-words">{getTemplateLabel(templateId)}</div>
                        <div className="text-xs text-white/60 truncate">#{templateId ?? "-"}</div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20 inline-block max-w-full truncate">
                        {channel}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <span className="px-2 py-1 rounded text-xs bg-purple-500/15 text-purple-200 border border-white/15 inline-block max-w-full truncate">
                        {scheduling}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <div className="text-sm break-words">{when}</div>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <span className={`px-2 py-1 rounded text-xs border inline-block max-w-full truncate ${getStatusStyle(status)}`}>
                        {status}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-right align-top">
                      <div className="flex flex-col gap-2 items-end">
                        <button
                          type="button"
                          onClick={() => onView?.(r)}
                          disabled={loading}
                          className="w-full px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white text-xs backdrop-blur-lg flex items-center justify-center gap-1 disabled:opacity-50"
                        >
                          <Eye className="w-4 h-4" />
                          {t("sales.scheduledMessages.actions.view")}
                        </button>

                        <button
                          type="button"
                          onClick={() => onCancel?.(r)}
                          disabled={loading || !cancelable}
                          className={[
                            "w-full px-3 py-1.5 rounded-lg text-xs backdrop-blur-lg flex items-center justify-center gap-1 disabled:opacity-50 border",
                            cancelable
                              ? "bg-red-500/20 hover:bg-red-500/30 border-red-400/30 text-red-100"
                              : "bg-white/5 border-white/10 text-white/50",
                          ].join(" ")}
                          title={
                            cancelable
                              ? t("sales.scheduledMessages.actions.cancelTitle")
                              : t("sales.scheduledMessages.actions.cancelDisabledTitle")
                          }
                        >
                          <Ban className="w-4 h-4" />
                          {t("sales.scheduledMessages.actions.cancel")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && rows.length === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={7}>
                    {t("sales.scheduledMessages.empty")}
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={7}>
                    {t("common.loading")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ Pagination Footer */}
     {/* ✅ Pagination Footer (i18n) */}
<div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between px-4 py-4 border-t border-white/10 bg-white/5">
  <div className="text-xs text-white/70">
    {safeTotal === 0 ? (
      <span>{t("sales.scheduledMessages.pagination.none")}</span>
    ) : (
      <span>
        {t("sales.scheduledMessages.pagination.showing", {
          start: startIndex,
          end: endIndex,
          total: safeTotal,
        })}
      </span>
    )}
  </div>

  <div className="flex items-center gap-2 justify-end">
    <button
      type="button"
      onClick={() => onPageChange?.(safePage - 1)}
      disabled={loading || safePage <= 1}
      className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white disabled:opacity-50 flex items-center gap-2"
      title={t("sales.scheduledMessages.pagination.prev")}
    >
      <ChevronRight className="w-4 h-4" />
      {t("sales.scheduledMessages.pagination.prev")}
    </button>

    <div className="flex items-center gap-1">
      {pageButtons.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPageChange?.(p)}
          disabled={loading}
          className={[
            "min-w-[40px] px-3 py-2 rounded-xl border text-sm",
            p === safePage
              ? "bg-purple-600/60 border-purple-300/30 text-white"
              : "bg-white/10 hover:bg-white/15 border-white/20 text-white",
            loading ? "opacity-50" : "",
          ].join(" ")}
          aria-current={p === safePage ? "page" : undefined}
          title={t("sales.scheduledMessages.pagination.pageOf", { page: p, pages: totalPages })}
        >
          {p}
        </button>
      ))}
    </div>

    <button
      type="button"
      onClick={() => onPageChange?.(safePage + 1)}
      disabled={loading || safePage >= Math.max(1, Number(totalPages) || 1)}
      className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white disabled:opacity-50 flex items-center gap-2"
      title={t("sales.scheduledMessages.pagination.next")}
    >
      {t("sales.scheduledMessages.pagination.next")}
      <ChevronLeft className="w-4 h-4" />
    </button>
  </div>
</div>

      </div>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";
